"use client";

import Script from "next/script";

/**
 * Google Tag Manager Component
 *
 * GTM provides a centralized way to manage all tracking scripts:
 * - Google Analytics 4 (add as a tag in GTM)
 * - Conversion tracking
 * - Custom events
 * - Third-party tools (Hotjar, Facebook Pixel, etc.)
 *
 * Setup:
 * 1. Create GTM container at tagmanager.google.com
 * 2. Get Container ID (GTM-XXXXXXX)
 * 3. Add NEXT_PUBLIC_GTM_ID to .env.local and Vercel
 * 4. In GTM, add GA4 Configuration tag with your GA4 Measurement ID
 *
 * Benefits over direct GA4:
 * - Add/modify tracking without code changes
 * - Marketing team can manage tags independently
 * - Easy to add other tools (Hotjar, Facebook Pixel, etc.)
 * - Built-in preview mode for testing
 */

interface GoogleTagManagerProps {
  gtmId?: string;
}

export function GoogleTagManager({ gtmId }: GoogleTagManagerProps) {
  const id = gtmId || process.env.NEXT_PUBLIC_GTM_ID;

  // Don't render if no ID (allow dev mode for GTM Preview testing)
  if (!id) {
    return null;
  }

  return (
    <>
      {/* GTM Script - Head */}
      <Script
        id="gtm-script"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${id}');
          `,
        }}
      />
    </>
  );
}

/**
 * GTM NoScript fallback for body
 * Required for users with JavaScript disabled
 */
export function GoogleTagManagerNoScript({ gtmId }: GoogleTagManagerProps) {
  const id = gtmId || process.env.NEXT_PUBLIC_GTM_ID;

  if (!id) {
    return null;
  }

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${id}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
        title="Google Tag Manager"
      />
    </noscript>
  );
}

/**
 * Push events to GTM dataLayer
 *
 * Usage:
 * ```ts
 * import { pushToDataLayer } from '@/components/analytics/GoogleTagManager';
 *
 * // Track purchase
 * pushToDataLayer({
 *   event: 'purchase',
 *   value: 99,
 *   currency: 'USD',
 *   transaction_id: 'abc123'
 * });
 *
 * // Track content access
 * pushToDataLayer({
 *   event: 'content_access',
 *   principle: 'gestalt-principles',
 *   access_type: 'premium'
 * });
 * ```
 */
export function pushToDataLayer(data: Record<string, unknown>): void {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Track custom events via GTM
 */
export function trackGTMEvent(
  eventName: string,
  parameters?: Record<string, string | number | boolean>
): void {
  pushToDataLayer({
    event: eventName,
    ...parameters,
  });
}

/**
 * Track page view via GTM (for SPA navigation)
 */
export function trackGTMPageView(url: string, title?: string): void {
  pushToDataLayer({
    event: "page_view",
    page_path: url,
    page_title: title,
  });
}

/**
 * Track purchase event
 */
export function trackGTMPurchase(
  value: number,
  transactionId: string,
  currency: string = "USD"
): void {
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: transactionId,
      value,
      currency,
      items: [
        {
          item_name: "Interface Audit - Full Report",
          item_id: "uxui-full-access",
          price: value,
          quantity: 1,
        },
      ],
    },
  });
}

/**
 * Track paywall view
 */
export function trackGTMPaywallView(principleSlug: string): void {
  pushToDataLayer({
    event: "paywall_view",
    principle: principleSlug,
    content_type: "premium_principle",
  });
}

/**
 * Track content access
 */
export function trackGTMContentAccess(
  principleSlug: string,
  isPremium: boolean
): void {
  pushToDataLayer({
    event: "content_access",
    principle: principleSlug,
    access_type: isPremium ? "premium" : "free",
  });
}

/**
 * Track search query
 */
export function trackGTMSearch(
  query: string,
  resultCount: number,
  locale: string = "en"
): void {
  pushToDataLayer({
    event: "search",
    search_term: query,
    search_result_count: resultCount,
    search_locale: locale,
  });
}

/**
 * Track search result click
 */
export function trackGTMSearchClick(
  query: string,
  principleSlug: string,
  position: number,
  locale: string = "en"
): void {
  pushToDataLayer({
    event: "search_click",
    search_term: query,
    click_principle: principleSlug,
    click_position: position,
    search_locale: locale,
  });
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
    gtag: (
      command: string,
      ...args: (string | Record<string, unknown>)[]
    ) => void;
  }
}
